import axios from "axios";
import "firebase/auth";
import {
  clearCookies,
  clearStorage,
  getFirebaseUser,
  getStorage,
} from "../auth/utils";

// ---------------------------------------------------------
// Config
// ---------------------------------------------------------
const baseUrl = process.env.REACT_APP_BASE_URL;

// Single-flight refresh and redirect guards
let refreshPromise = null;
let isRedirectingToLogin = false;

// Decode JWT payload to read exp
function parseJwt(token) {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const data = parseJwt(token);
  if (!data || !data.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  const SKEW_SECONDS = 60; // refresh 1 minute early
  return now >= data.exp - SKEW_SECONDS;
}

// ---------------------------------------------------------
// REQUEST interceptor (auth + refresh)
// ---------------------------------------------------------
axios.interceptors.request.use(
  async (config) => {
    // Skip auth for Firebase refresh endpoint or when explicitly disabled
    if (
      config?.url?.includes("securetoken.googleapis.com") ||
      config.headers["x-requires-auth"] !== "true"
    ) {
      return config;
    }

    let token = getStorage().getItem("token");

    const needsRefresh = !token || isTokenExpired(token);

    if (needsRefresh) {
      if (!refreshPromise) {
        refreshPromise = getFirebaseUser().then(async (user) => {
          if (!user) {
            // No firebase user => unauthenticated
            const err = new Error("UNAUTHENTICATED_NO_USER");
            err.code = "UNAUTHENTICATED";
            throw err;
          }
          const newToken = await user.getIdToken(true);
          getStorage().setItem("token", newToken);
          return newToken;
        });
      }

      try {
        token = await refreshPromise;
      } catch (e) {
        // DO NOT hard-redirect here; let response interceptor / app handle it
        // We still clear client-side storage to avoid loops.
        clearStorage();
        clearCookies();

        // Let the request reject; caller (and response interceptor) will react
        throw e;
      } finally {
        refreshPromise = null;
      }
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------
// RESPONSE interceptor (auth failures → single redirect)
// ---------------------------------------------------------
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const status = error?.response?.status;

    if ((status === 401 || status === 403) && !isRedirectingToLogin) {
      isRedirectingToLogin = true;
      try {
        // Clear tokens; let your auth flow show login page
        getStorage().removeItem("token");
        getStorage().removeItem("refreshToken");
      } finally {
        // Prefer triggering your AuthContext logout if available.
        // If not, fallback to location change:
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// ---------------------------------------------------------
// Public API
// ---------------------------------------------------------
export function axiosGraphQL(payload, { noAuth = false } = {}) {
  return axios.post(`${baseUrl}/graphql`, payload, {
    headers: {
      ...(noAuth ? {} : { "x-requires-auth": "true" }),
    },
  });
}

export function axiosGet(url, { noAuth = false } = {}) {
  return axios.get(`${baseUrl}${url}`, {
    headers: {
      ...(noAuth ? {} : { "x-requires-auth": "true" }),
    },
  });
}

export function axiosPost(url, payload, { noAuth = false } = {}) {
  return axios.post(`${baseUrl}${url}`, payload, {
    headers: {
      ...(noAuth ? {} : { "x-requires-auth": "true" }),
    },
  });
}
